import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc, 
  serverTimestamp, 
  orderBy, 
  where,
  arrayUnion,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ScholarshipReviews from './ScholarshipReviews';

const ScholarshipApplicationsManagement = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedScholarship, setSelectedScholarship] = useState('all');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [bulkCommentText, setBulkCommentText] = useState('');
  const navigate = useNavigate();
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [searchEmail, setSearchEmail] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [jumpToPage, setJumpToPage] = useState('');
  const [sortField, setSortField] = useState('appliedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Sort applications
  const sortedApplications = [...applications].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Filtered and sorted applications
  const filteredApplications = sortedApplications.filter(app => {
    if (!searchEmail.trim()) return true;
    return (
      app.email &&
      app.email.toLowerCase().includes(searchEmail.trim().toLowerCase())
    );
  });

  // Pagination calculations should use filteredApplications
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredApplications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle jump to page
  const handleJumpToPage = (e) => {
    e.preventDefault();
    const pageNumber = parseInt(jumpToPage);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setJumpToPage('');
    }
  };

  // Handle sort
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Load scholarships and applications
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch scholarships
        const scholarshipsQuery = query(collection(db, "scholarships"), orderBy("title"));
        const scholarshipsSnapshot = await getDocs(scholarshipsQuery);
        const scholarshipsList = scholarshipsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setScholarships(scholarshipsList);

        // Fetch applications
        let applicationsQuery;
        if (selectedStatus !== 'all' && selectedScholarship !== 'all') {
          applicationsQuery = query(
            collection(db, "scholarship_applications"),
            where("status", "==", selectedStatus),
            where("scholarshipId", "==", selectedScholarship),
            orderBy("appliedAt", "desc")
          );
        } else if (selectedStatus !== 'all') {
          applicationsQuery = query(
            collection(db, "scholarship_applications"),
            where("status", "==", selectedStatus),
            orderBy("appliedAt", "desc")
          );
        } else if (selectedScholarship !== 'all') {
          applicationsQuery = query(
            collection(db, "scholarship_applications"),
            where("scholarshipId", "==", selectedScholarship),
            orderBy("appliedAt", "desc")
          );
        } else {
          applicationsQuery = query(
            collection(db, "scholarship_applications"),
            orderBy("appliedAt", "desc")
          );
        }

        const applicationsSnapshot = await getDocs(applicationsQuery);
        const applicationsList = applicationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setApplications(applicationsList);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };

    fetchData();
  }, [selectedStatus, selectedScholarship]);

  // View application details
  const viewApplicationDetails = async (application) => {
    setSelectedApplication(application);
    setApplicationDetails(null); // Reset details before loading new ones
    setLoadingDetails(true);
    // Prevent double fetching if already selected
    if (selectedApplication?.id === application.id) {
        setLoadingDetails(false);
        return;
    }
    try {
      // Get user details
      let enhancedApplication = { ...application };
      
      if (application.userId) {
        const userDoc = await getDoc(doc(db, "users", application.userId));
        if (userDoc.exists()) {
          enhancedApplication.userDetails = userDoc.data();
        }
      }
      
      // Get scholarship details
      if (application.scholarshipId) {
        const scholarshipDoc = await getDoc(doc(db, "scholarships", application.scholarshipId));
        if (scholarshipDoc.exists()) {
          enhancedApplication.scholarshipDetails = scholarshipDoc.data();
        } else {
          // If document doesn't exist, try to find it in our local state
          const scholarship = scholarships.find(s => s.id === application.scholarshipId);
          if (scholarship) {
            enhancedApplication.scholarshipDetails = scholarship;
          }
        }
      }
      
      setApplicationDetails(enhancedApplication);
    } catch (error) {
      console.error("Error fetching application details:", error);
    }
    
    setLoadingDetails(false);
  };

  // Update application status
  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await updateDoc(doc(db, "scholarship_applications", applicationId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setApplications(prev => prev.map(app => 
        app.id === applicationId 
          ? { ...app, status: newStatus, updatedAt: serverTimestamp() } 
          : app
      ));
      
      if (selectedApplication && selectedApplication.id === applicationId) {
        setSelectedApplication(prev => ({
          ...prev,
          status: newStatus,
          updatedAt: serverTimestamp()
        }));
        setApplicationDetails(prev => ({
          ...prev,
          status: newStatus,
          updatedAt: serverTimestamp()
        }));
      }
    } catch (error) {
      console.error("Error updating application status:", error);
    }
  };

  // Handle selection of an application for bulk actions
  const handleSelectApplication = (applicationId) => {
    setSelectedApplications(prev => 
      prev.includes(applicationId) 
        ? prev.filter(id => id !== applicationId) 
        : [...prev, applicationId]
    );
  };

  // Handle select all applications
  const handleSelectAllApplications = () => {
    if (selectedApplications.length === filteredApplications.length) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(filteredApplications.map(app => app.id));
    }
  };

  // Add a bulk comment
  const addBulkAdminComment = async () => {
    if (!bulkCommentText.trim() || selectedApplications.length === 0) return; // Prevent empty comments or no selections
    
    try {
      const batch = writeBatch(db);
      const comment = {
        text: bulkCommentText,
        adminId: user?.uid || 'unknown',
        timestamp: serverTimestamp(),
      };
      
      selectedApplications.forEach(appId => {
        const applicationRef = doc(db, "scholarship_applications", appId);
        batch.update(applicationRef, {
          adminComments: arrayUnion(comment)
        });
      });
      
      await batch.commit();
      
      // Clear selected applications and comment text after successful commit
      const addedCommentLocal = { ...comment, timestamp: new Date() }; // Simulate serverTimestamp locally
      
      // Update local state
      setApplications(prev => prev.map(app => {
        if (selectedApplications.includes(app.id)) {
          return {
            ...app,
            adminComments: [...(app.adminComments || []), addedCommentLocal]
          };
        }
        return app;
      }));
      
      if (selectedApplication && selectedApplications.includes(selectedApplication.id)) {
         setApplicationDetails(prev => ({
            ...prev,
            adminComments: [...(prev.adminComments || []), addedCommentLocal]
         }));
      }
    } catch (error) {
      console.error("Error adding bulk comments:", error);
      // Handle error
    }
  };

  // Add a new comment
  const addAdminComment = async (applicationId, commentText) => {
    if (!commentText.trim()) return; // Prevent adding empty comments
    try {
      const comment = {
        text: commentText,
        adminId: user?.uid || 'unknown',
        timestamp: serverTimestamp(),
      };
      
      const applicationRef = doc(db, "scholarship_applications", applicationId);
      
      await updateDoc(applicationRef, {
        adminComments: arrayUnion(comment)
      });
      
      // Update local state to reflect the new comment
      setApplicationDetails(prev => ({
        ...prev,
        adminComments: [...(prev.adminComments || []), { ...comment, timestamp: new Date() }]
      }));
    } catch (error) {
      console.error("Error adding comment:", error);
      // Handle error (e.g., show a message to the admin user)
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Format date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Utility function to convert array of objects to CSV
  function arrayToCSV(data) {
    if (!data.length) return '';
    // Collect all unique keys for header
    const keys = Array.from(new Set(data.flatMap(obj => Object.keys(obj))));
    const header = keys.join(',');
    const rows = data.map(row =>
      keys.map(k => {
        let val = row[k];
        if (val === undefined || val === null) return '';
        if (typeof val === 'object') return '"' + JSON.stringify(val).replace(/"/g, '""') + '"';
        return '"' + String(val).replace(/"/g, '""') + '"';
      }).join(',')
    );
    return [header, ...rows].join('\r\n');
  }

  function downloadCSV(csv, filename) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  const handleExport = () => {
    if (!filteredApplications.length) return;
    // Pick main fields for export
    const exportData = filteredApplications.map(app => ({
      fullName: app.fullName,
      email: app.email,
      phone: app.phone,
      status: app.status,
      scholarship: scholarships.find(s => s.id === app.scholarshipId)?.title || app.scholarshipId,
      appliedAt: app.appliedAt && app.appliedAt.toDate ? app.appliedAt.toDate().toISOString() : app.appliedAt,
      ...app // include all other fields for completeness
    }));
    const csv = arrayToCSV(exportData);
    downloadCSV(csv, 'scholarship_applications_export.csv');
  };

  // Add this new component for document viewing
  const DocumentViewer = ({ url, title }) => {
    const [showPreview, setShowPreview] = useState(false);
    const [error, setError] = useState(false);
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    
    // Determine file type based on URL or extension
    const getFileType = (url) => {
      if (!url) return 'unknown';
      if (url.includes('.pdf')) return 'pdf';
      if (url.match(/\.(jpeg|jpg|gif|png)$/i)) return 'image';
      if (url.includes('/image/upload/')) return 'image';
      return 'document';
    };
    
    const fileType = getFileType(url);
    
    const handleView = () => {
      if (fileType === 'pdf') {
        // For PDFs, open in iframe modal
        setShowPdfViewer(true);
      } else {
        // For other file types (images, documents), open in new tab
        window.open(url, '_blank');
      }
    };
    
    const handlePreview = () => {
      setShowPreview(true);
    };
    
    return (
      <div className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex flex-col items-center text-center">
        {fileType === 'pdf' ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[rgb(130,88,18)] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ) : fileType === 'image' ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[rgb(130,88,18)] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[rgb(130,88,18)] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        )}
        <span className="font-medium">{title}</span>
        <div className="mt-2 flex space-x-2">
          <button 
            onClick={handleView}
            className="px-3 py-1 bg-[rgb(130,88,18)] text-white rounded-md text-sm hover:bg-[rgb(110,68,0)]"
          >
            View
          </button>
          {fileType === 'image' && (
            <button 
              onClick={handlePreview}
              className="px-3 py-1 border border-[rgb(130,88,18)] text-[rgb(130,88,18)] rounded-md text-sm"
            >
              Preview
            </button>
          )}
        </div>
        
        {/* Image Preview Modal */}
        {showPreview && fileType === 'image' && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
            <div className="bg-white p-2 rounded-lg max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="relative">
                <img 
                  src={url} 
                  alt={title} 
                  className="max-w-full h-auto"
                  onError={() => setError(true)}
                />
                {error && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <p className="text-red-500">Failed to load image</p>
                  </div>
                )}
                <button 
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md"
                  onClick={() => setShowPreview(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* PDF Viewer Modal */}
        {showPdfViewer && fileType === 'pdf' && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowPdfViewer(false)}>
            <div className="bg-white p-2 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center border-b pb-2 mb-2">
                <h3 className="text-lg font-semibold">{title}</h3>
                <button 
                  className="bg-white rounded-full p-1 shadow-md"
                  onClick={() => setShowPdfViewer(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <iframe src={url} title={title} className="flex-grow" style={{ width: '100%', height: '100%' }} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Scholarship Applications</h1>
      
      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="waitlisted">Waitlisted</option>
              </select>
            </div>
            <button
              onClick={handleExport}
              className="ml-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              title="Export filtered applications as CSV"
              disabled={filteredApplications.length === 0}
            >
              Export
            </button>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Scholarship</label>
            <select
              value={selectedScholarship}
              onChange={(e) => setSelectedScholarship(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
            >
              <option value="all">All Scholarships</option>
              {scholarships.map(scholarship => (
                <option key={scholarship.id} value={scholarship.id}>
                  {scholarship.title || scholarship.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* Email Search Input */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search by Email</label>
          <input
            type="text"
            value={searchEmail}
            onChange={e => {
              setSearchEmail(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            placeholder="Enter applicant email..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
          />
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Applications List */}
        <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Applications</h2>
          
          {loading ? (
            <div className="flex justify-center my-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
            </div>
          ) : filteredApplications.length > 0 ? (
            <div className="space-y-4">
              {/* Bulk Comment Section */}
              <div className="mb-4 p-4 border rounded-md bg-gray-50">
                <h3 className="text-lg font-semibold mb-2">Add Bulk Comment</h3>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)] mb-2"
                  rows="3"
                  placeholder="Enter comment for selected applications..."
                  value={bulkCommentText}
                  onChange={(e) => setBulkCommentText(e.target.value)}
                ></textarea>
                <button
                  onClick={addBulkAdminComment}
                  disabled={selectedApplications.length === 0 || !bulkCommentText.trim()}
                  className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:bg-[rgb(110,75,15)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Comment to Selected ({selectedApplications.length})
                </button>
              </div>

              {/* Select All Checkbox */}
              <div className="flex items-center mb-4">
                <input 
                  type="checkbox" 
                  className="form-checkbox h-5 w-5 text-[rgb(130,88,18)] rounded focus:ring-[rgb(130,88,18)]"
                  checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
                  onChange={handleSelectAllApplications}
                />
                <label className="ml-2 text-sm font-medium text-gray-700">Select All ({selectedApplications.length}/{filteredApplications.length} selected)</label>
              </div>

              {currentItems.map(application => {
                // Find the scholarship name
                const scholarship = scholarships.find(s => s.id === application.scholarshipId);
                
                return (
                  <div 
                    key={application.id} 
                    className={`p-4 border rounded-md cursor-pointer transition-colors ${
                      selectedApplicationId === application.id
                        ? 'border-[rgb(130,88,18)] bg-[rgb(130,88,18)]/10'
                        : 'border-gray-200 hover:border-[rgb(130,88,18)] hover:bg-[rgb(130,88,18)]/5'
                    }`}
                    onClick={() => setSelectedApplicationId(application.id)}
                  >
                    {/* Checkbox for individual selection */}
                    <div className="flex items-start mb-2">
                      <input 
                        type="checkbox" 
                        className="form-checkbox h-5 w-5 text-[rgb(130,88,18)] rounded focus:ring-[rgb(130,88,18)]"
                        checked={selectedApplications.includes(application.id)}
                        onChange={() => handleSelectApplication(application.id)}
                        onClick={(e) => e.stopPropagation()} // Prevent triggering the view details click
                      />
                      <div className="ml-3 flex-grow">
                        <h3 className="font-medium mb-1">{application.fullName || 'Unknown'}</h3>
                        <p className="text-sm text-gray-600">For: <span className="font-medium">{scholarship?.title || scholarship?.name || 'Unknown Scholarship'}</span></p>
                        {application.adminComments && application.adminComments.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-[rgb(130,88,18)]" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.1c1.4-1.4 2.371-2.705 2.758-4.011C7.5 4.06 10.742 2 14 2c3.418 0 6 3.134 6 7z" clipRule="evenodd" />
                            </svg>
                            {application.adminComments.length} comment{application.adminComments.length > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                   
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">{formatDate(application.appliedAt)}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        application.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                        application.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                        application.status === 'approved' ? 'bg-green-100 text-green-800' :
                        application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        application.status === 'waitlisted' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {application.status?.replace('_', ' ').charAt(0).toUpperCase() + 
                          application.status?.replace('_', ' ').slice(1) || 'Unknown'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>Select an application to view details</p>
            </div>
          )}

          {/* Pagination Controls */}
          {filteredApplications.length > 0 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border disabled:opacity-50 hover:bg-gray-100"
                >
                  Previous
                </button>
                <span className="text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded border disabled:opacity-50 hover:bg-gray-100"
                >
                  Next
                </button>
              </div>

              <form onSubmit={handleJumpToPage} className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={jumpToPage}
                  onChange={(e) => setJumpToPage(e.target.value)}
                  placeholder="Go to page"
                  className="w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-[rgb(130,88,18)]"
                />
                <button
                  type="submit"
                  className="px-3 py-1 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
                >
                  Go
                </button>
              </form>
            </div>
          )}

          {/* Sort Controls */}
          <div className="mt-4 flex items-center gap-4">
            <span className="text-sm text-gray-600">Sort by:</span>
            <button
              onClick={() => handleSort('appliedAt')}
              className={`px-3 py-1 rounded border ${
                sortField === 'appliedAt' ? 'bg-[rgb(130,88,18)] text-white' : 'hover:bg-gray-100'
              }`}
            >
              Date {sortField === 'appliedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('fullName')}
              className={`px-3 py-1 rounded border ${
                sortField === 'fullName' ? 'bg-[rgb(130,88,18)] text-white' : 'hover:bg-gray-100'
              }`}
            >
              Name {sortField === 'fullName' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('status')}
              className={`px-3 py-1 rounded border ${
                sortField === 'status' ? 'bg-[rgb(130,88,18)] text-white' : 'hover:bg-gray-100'
              }`}
            >
              Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
          </div>
        </div>
        
        {/* Application Details */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-md">
          {selectedApplicationId ? (
            <ScholarshipReviews applicationId={selectedApplicationId} embedded={true} />
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>Select an application to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScholarshipApplicationsManagement; 